import React, { FC, useCallback, useEffect, useState } from 'react';

import { Container } from '../../components/container/container';
import {
  EmptyFilter,
  ServicesBase,
  ServicesTitleBase,
  SideFilterBase,
  StyledGridServiceCard,
  TopServicesBase,
  TopServicesWrap,
  TotalServices,
  LoadingServices,
} from './style';
import {
  Button,
  CardServices,
  EnumPagination,
  LoadingSpin,
  SideFilter,
  Title,
  Row,
  Col,
} from '@plataformaxvia/design-system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Grid } from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import WarningIcon from '@mui/icons-material/Warning';
import { WarningModal } from '../../components/warning-modal/WarningModal';
import { ContainerModalAction } from '../../components/warning-modal/style';
import { ICategory } from '../../apis/categorys';
import { IDepartment } from '../../apis/department';
import { IFilter } from '../../hooks/use-services-filters';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useDepartmentPdf } from '../../hooks/use-service-charter';
import { Tag } from '../../components/tag/tag';

interface SwitchDataProps {
  id: string;
  label: string;
  checked: boolean;
  setChecked: React.Dispatch<React.SetStateAction<boolean>>;
  disabled: boolean;
}

interface DropdownItem {
  id: string;
  label: string;
  checked: boolean;
  setChecked: () => void;
}

interface DropdownData {
  id: string;
  title: string;
  active: boolean;
  onToggle: () => void;
  dropdownItems: DropdownItem[];
}

interface CardServicesPros {
  title: string;
  category: string;
  onInfoClick: () => void;
  onRequestClick: () => void;
  isDigital: boolean;
  isOnline: boolean;
  totalServices: number;
}

interface Suggestions {
  label: string;
  action: () => void;
}

export interface RelevantServicesProps {
  services?: CardServicesPros[];
  switchData: SwitchDataProps[];
  dropdownData: DropdownData[];
  setCurrentPage: (page: number) => void;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  setSearch: (value: string) => void;
  onSearchClick: (customSearch?: string) => void;
  onSearchClear: () => void;
  title: string;
  element?: React.ReactNode;
  totalServices?: boolean;
  isRedirectWarningModalOpen?: boolean;
  toggleRedirectWarningModal: () => void;
  handleRedirectWarningModalConfirm: () => void;
  selectedCategory: string;
  categories: ICategory[];
  selectedDepartment: string;
  departments: IDepartment[];
  filters: IFilter;
  selectedTag: string;
  resetFilters: () => void;
  handleRemoveTag: () => void;
  suggestions: Suggestions[];
  isTotalServicesLoading: boolean;
}

export const Services: FC<RelevantServicesProps> = ({
  services,
  setCurrentPage,
  currentPage,
  totalPages,
  isLoading,
  setSearch,
  onSearchClick,
  onSearchClear,
  switchData,
  dropdownData,
  title,
  element,
  totalServices,
  isRedirectWarningModalOpen = false,
  toggleRedirectWarningModal,
  handleRedirectWarningModalConfirm,
  selectedCategory,
  categories,
  selectedDepartment,
  departments,
  filters,
  selectedTag,
  handleRemoveTag,
  suggestions,
  isTotalServicesLoading,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1200));
  const isSmallScreenFilter = useMediaQuery(theme.breakpoints.down(780));
  const [activeFilter, setActiveFilter] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [showPdfDownloadButton, setShowPdfDownloadButton] = useState(true);
  const [colSizes, setColSizes] = useState([6, 6]);
  const totalServicesCount = services?.[0]?.totalServices;
  const totalServicesText = isTotalServicesLoading
    ? 'Contando...'
    : totalServicesCount !== undefined
    ? `${totalServicesCount} serviço(s)`
    : 'Contagem indisponível';

  useEffect(() => {
    if (selectedDepartment && services?.length) {
      setShowPdfDownloadButton(true);
    } else {
      setShowPdfDownloadButton(false);
    }
  }, [selectedDepartment, services]);

  const { refetch, isFetching } = useDepartmentPdf(selectedDepartment);

  const handlePdfDownload = async () => {
    try {
      const result = await refetch();
      const blob = result.data;

      if (!blob) {
        console.error('No PDF returned');
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'carta_de_servicos.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const handleDropdownToggle = (id: string) => {
    setActiveDropdownId((prev) => (prev === id ? null : id));
  };

  const controlledDropdownData = dropdownData.map((dropdown) => ({
    ...dropdown,
    active: activeDropdownId === dropdown.id,
    onToggle: () => handleDropdownToggle(dropdown.id),
  }));

  const handleToggleFilter = useCallback(() => {
    setActiveFilter(!activeFilter);
  }, [activeFilter]);

  const categoryDisplay = categories.find(
    (category) => category.slug === selectedCategory
  )?.name;
  const departmentDisplay = departments.find(
    (department) => department.slug === selectedDepartment
  )?.name;

  const getCardServiceTitle = useCallback(() => {
    if (categoryDisplay && departmentDisplay) {
      return `${categoryDisplay} - ${departmentDisplay}`;
    }

    if (categoryDisplay) {
      return categoryDisplay;
    }

    if (departmentDisplay) {
      return departmentDisplay;
    }

    return title;
  }, [title, categoryDisplay, departmentDisplay]);

  const onChangeInputValue = (newValue: string) => {
    setInputValue(newValue);
  };

  useEffect(() => {
    onChangeInputValue(filters.searchQuery);
    setSearch(filters.searchQuery);
  }, [filters.searchQuery, setSearch]);

  useEffect(() => {
    if (selectedCategory || selectedDepartment) {
      setInputValue(' ');
      setSearch('');
      setTimeout(() => {
        setInputValue('');
      }, 0); // próxima render
    }
  }, [selectedCategory, selectedDepartment, setSearch]);

  useEffect(() => {
    setSearch(inputValue);
  }, [inputValue, setSearch]);

  const clearSearch = useCallback(() => {
    setInputValue('');
    onSearchClear();
  }, [onSearchClear]);

  useEffect(() => {
    if (getCardServiceTitle().length > 15) {
      setColSizes([6, 6]);
    } else {
      setColSizes([4, 8]);
    }
  }, [getCardServiceTitle]);

  return (
    <TopServicesWrap>
      <WarningModal
        headerTitle="Atenção!"
        icon={<WarningIcon />}
        headerBgColor="var(--color-background-card-warning-header)"
        onCloseModal={toggleRedirectWarningModal}
        modalOpen={isRedirectWarningModalOpen}
      >
        <p>
          Você está sendo redirecionado para um link externo. Tem certeza que
          deseja continuar?
        </p>

        <ContainerModalAction>
          <Button
            onClick={handleRedirectWarningModalConfirm}
            label="Confirmar redirecionamento"
          />
        </ContainerModalAction>
      </WarningModal>
      <Container>
        <TopServicesBase>
          <SideFilterBase
            className={'filter-content xvia-catalog-search-input__input-list'}
            style={{ display: 'block', flexShrink: 0 }}
          >
            <SideFilter
              title="Filtrar"
              aria-label="Filtrar serviços"
              searchPlaceholder="ex: emprego"
              searchValue={inputValue}
              onSearchChange={(event: any) =>
                onChangeInputValue(event.target.value)
              }
              onSearchClick={onSearchClick}
              onSearchClear={clearSearch}
              switchData={switchData}
              dropdownData={controlledDropdownData}
              active={activeFilter}
              handleToggle={handleToggleFilter}
              insideAccordion={isSmallScreenFilter}
              suggestions={suggestions}
            />
          </SideFilterBase>
          <ServicesBase>
            <ServicesTitleBase>
              <Row justifyContent="space-between" style={{ width: '100%' }}>
                <Col base={colSizes[0]}>
                  <Title title={getCardServiceTitle()} as="h2" />
                </Col>

                <Col
                  base={colSizes[1]}
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  {totalServices ? (
                    <TotalServices>
                      <Row
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                      >
                        {services && services.length > 0
                          ? totalServicesText
                          : '0 serviço(s)'}
                      </Row>

                      <Row
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                      >
                        <Button
                          iconStart={<PictureAsPdfIcon />}
                          onClick={handlePdfDownload}
                          style={{
                            display: showPdfDownloadButton ? 'flex' : 'none',
                          }}
                          label="Baixar Carta de Serviços"
                          variant="primary"
                          size="small"
                          loading={isFetching}
                          disabled={isFetching}
                        />
                      </Row>
                    </TotalServices>
                  ) : (
                    <>
                      <Button
                        iconStart={<PictureAsPdfIcon />}
                        onClick={handlePdfDownload}
                        style={{
                          display: showPdfDownloadButton ? 'flex' : 'none',
                        }}
                        label="Baixar Carta de Serviços"
                        variant="primary"
                        size="small"
                        loading={isFetching}
                        disabled={isFetching}
                      />
                      {element}
                    </>
                  )}
                </Col>
                {selectedTag && (
                  <Row
                    style={{
                      marginBottom: '16px',
                      width: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Tag href="#" onClose={handleRemoveTag}>
                      {selectedTag}
                    </Tag>
                  </Row>
                )}
              </Row>
            </ServicesTitleBase>
            <StyledGridServiceCard container spacing={3}>
              {services &&
                services.length > 0 &&
                services?.map((service, index) => (
                  <Grid item xs={12} sm={isSmallScreen ? 12 : 6} key={index}>
                    <CardServices {...service} />
                  </Grid>
                ))}
              {isLoading && (
                <LoadingServices>
                  <LoadingSpin />
                  Carregando...
                </LoadingServices>
              )}
              {!isLoading && services && services.length === 0 && (
                <EmptyFilter>
                  <FilterAltOffIcon />
                  Nenhum resultado encontrado para os filtros selecionados.
                  Tente ajustar os critérios de busca.
                </EmptyFilter>
              )}
            </StyledGridServiceCard>
            {totalPages > 1 && (
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <EnumPagination
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  setCurrentPage={setCurrentPage}
                  totalPages={totalPages}
                />
              </Grid>
            )}
          </ServicesBase>
        </TopServicesBase>
      </Container>
    </TopServicesWrap>
  );
};
